/**
 * Comprime una imagen en el navegador hasta que no exceda `maxBytes`.
 * - Re-encodea a JPEG (mejor compresión que PNG para fotos).
 * - Conserva proporción y orientación EXIF (handled by createImageBitmap).
 * - Aplica reducción iterativa: primero baja calidad, después escala lado mayor.
 *
 * @param {File} file       Archivo de imagen original.
 * @param {object} [opts]
 * @param {number} [opts.maxBytes=2*1024*1024] Tamaño máximo objetivo en bytes.
 * @param {number} [opts.maxDimension=2400]    Lado máximo inicial en px.
 * @param {number} [opts.minQuality=0.4]       Calidad JPEG mínima.
 * @returns {Promise<File>} Archivo comprimido (puede ser el original si ya cumple).
 */
export async function compressImageToMaxSize(file, opts = {}) {
  const maxBytes = opts.maxBytes ?? 2 * 1024 * 1024;
  let maxDim = opts.maxDimension ?? 2400;
  const minQuality = opts.minQuality ?? 0.4;

  if (!file || !file.type.startsWith("image/")) return file;
  if (file.size <= maxBytes && file.type === "image/jpeg") return file;

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file; // fallback: subir tal cual

  let { width, height } = bitmap;
  // Loop: calidad descendente; si no llega, reescalamos lado mayor y reseteamos calidad.
  for (let pass = 0; pass < 6; pass++) {
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);

    let quality = 0.9;
    while (quality >= minQuality) {
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
      if (!blob) break;
      if (blob.size <= maxBytes) {
        const newName = file.name.replace(/\.[^.]+$/, "") + ".jpg";
        return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
      }
      quality -= 0.1;
    }
    // No alcanzó: bajamos resolución y probamos de nuevo
    maxDim = Math.round(maxDim * 0.8);
    if (maxDim < 600) break;
  }
  // Último intento: devuelve la versión mínima generada (puede seguir por encima si es una imagen enorme)
  const canvas = document.createElement("canvas");
  const scale = Math.min(1, 600 / Math.max(width, height));
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", minQuality));
  return new File([blob], (file.name.replace(/\.[^.]+$/, "") + ".jpg"), { type: "image/jpeg" });
}
